import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Star, StarOff, Trash2, Users } from "lucide-react";

import api from "../../api/Client";
import { API_BASE } from "../../config";
import { useSileo } from "../../components/Sileo";
import styles from "./FavoritesPage.module.css";

type Coach = {
  id: number;
  name: string;
  profile_photo?: string | null;
  service?: string | null;
  centro?: string | null;
};

type PreferredCoach = {
  coach_id: number;
  priority?: number | null;
};

type PreferredCoachResponse = {
  preferred_coach_id?: number | null;
  preferred_coaches?: PreferredCoach[];
};

type FavoriteWithCoach = {
  coach: Coach;
  priority: number;
};

export default function FavoritesPage() {
  const sileo = useSileo();

  const [loading, setLoading] = useState(true);
  const [savingCoachId, setSavingCoachId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [favorites, setFavorites] = useState<PreferredCoach[]>([]);
  const [selectedCenter, setSelectedCenter] = useState<string>("all");

  useEffect(() => {
    void bootstrap();
  }, []);

  async function bootstrap() {
    setLoading(true);
    setError(null);
    try {
      const [coachesResponse, favoritesResponse] = await Promise.all([
        api.get<Coach[]>("/api/customers/reservations/coaches", { params: { only_training: 1 } }),
        api.get<PreferredCoachResponse>("/api/customers/reservations/preferred-coach")
      ]);

      const coachList = Array.isArray(coachesResponse.data) ? coachesResponse.data : [];
      setCoaches(coachList);
      setFavorites(normalizeFavorites(favoritesResponse.data?.preferred_coaches));
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo cargar favoritos.";
      setError(message);
      setFavorites([]);
      sileo.error(message);
    } finally {
      setLoading(false);
    }
  }

  function normalizeFavorites(items?: PreferredCoach[] | null): PreferredCoach[] {
    if (!Array.isArray(items)) {
      return [];
    }
    return items
      .filter((item) => Number.isInteger(Number(item.coach_id)))
      .map((item) => ({
        coach_id: Number(item.coach_id),
        priority: Number.isInteger(Number(item.priority)) ? Number(item.priority) : null
      }))
      .sort((left, right) => (left.priority ?? 999) - (right.priority ?? 999))
      .slice(0, 3)
      .map((item, index) => ({ ...item, priority: index + 1 }));
  }

  const favoriteIds = useMemo(() => new Set(favorites.map((item) => item.coach_id)), [favorites]);
  const nextPriority = favorites.length < 3 ? favorites.length + 1 : null;

  const centers = useMemo(() => {
    const set = new Set<string>();
    coaches.forEach((coach) => {
      const value = String(coach.centro || "").trim().toLowerCase();
      if (value) {
        set.add(value);
      }
    });
    return Array.from(set).sort((left, right) => left.localeCompare(right));
  }, [coaches]);

  const filteredCoaches = useMemo(() => {
    return coaches.filter((coach) => {
      if (selectedCenter === "all") {
        return true;
      }
      return String(coach.centro || "").trim().toLowerCase() === selectedCenter;
    });
  }, [coaches, selectedCenter]);

  const favoriteCards = useMemo<FavoriteWithCoach[]>(() => {
    return favorites
      .map((favorite, index) => {
        const coach = coaches.find((candidate) => candidate.id === favorite.coach_id);
        if (!coach) {
          return null;
        }
        return {
          coach,
          priority: index + 1
        };
      })
      .filter((item): item is FavoriteWithCoach => Boolean(item));
  }, [favorites, coaches]);

  async function addOrMoveFavorite(coachId: number, priority?: number) {
    setSavingCoachId(coachId);
    try {
      const response = await api.post<PreferredCoachResponse>("/api/customers/reservations/preferred-coach", {
        coach_id: coachId,
        priority
      });

      setFavorites(normalizeFavorites(response.data?.preferred_coaches));
      if (favoriteIds.has(coachId)) {
        sileo.success("Favoritos actualizados.");
      } else {
        sileo.success("Entrenador anadido a favoritos.");
      }
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo guardar el favorito.";
      sileo.error(message);
      setError(message);
    } finally {
      setSavingCoachId(null);
    }
  }

  async function removeFavorite(coachId: number) {
    setSavingCoachId(coachId);
    try {
      const response = await api.delete<PreferredCoachResponse>(`/api/customers/reservations/preferred-coach/${coachId}`);
      setFavorites(normalizeFavorites(response.data?.preferred_coaches));
      sileo.info("Favorito eliminado.");
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || "No se pudo eliminar el favorito.";
      sileo.error(message);
      setError(message);
    } finally {
      setSavingCoachId(null);
    }
  }

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroBadge}>
          <Users size={14} />
          Entrenadores favoritos
        </div>
        <h1>Elige hasta 3 entrenadores de referencia</h1>
        <p>
          Priorizamos tus preferencias cuando hay disponibilidad. Puedes modificar el orden en cualquier momento.
        </p>
      </section>

      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <h2>Tus favoritos</h2>
          <span className={styles.limitTag}>{favorites.length}/3</span>
        </header>

        {loading && (
          <div className={styles.stateRow}>
            <Loader2 size={16} className={styles.spin} />
            <span>Cargando favoritos...</span>
          </div>
        )}

        {!loading && favoriteCards.length === 0 && (
          <p className={styles.emptyText}>Todavia no has elegido entrenadores favoritos.</p>
        )}

        {!loading && favoriteCards.length > 0 && (
          <div className={styles.favoriteList}>
            {favoriteCards.map(({ coach, priority }) => (
              <article key={coach.id} className={styles.favoriteItem}>
                <CoachAvatar coach={coach} />
                <div className={styles.favoriteInfo}>
                  <strong>{coach.name}</strong>
                  <span>Prioridad {priority}</span>
                </div>
                <select
                  className={styles.prioritySelect}
                  value={priority}
                  onChange={(event) => {
                    void addOrMoveFavorite(coach.id, Number(event.target.value));
                  }}
                  disabled={savingCoachId === coach.id}
                >
                  {[1, 2, 3].map((position) => (
                    <option key={position} value={position} disabled={position > favorites.length}>
                      P{position}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => {
                    void removeFavorite(coach.id);
                  }}
                  disabled={savingCoachId === coach.id}
                  aria-label="Eliminar favorito"
                >
                  {savingCoachId === coach.id ? <Loader2 size={16} className={styles.spin} /> : <Trash2 size={16} />}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <h2>Entrenadores disponibles</h2>
          <div className={styles.centerTabs}>
            <button
              type="button"
              className={`${styles.centerTab} ${selectedCenter === "all" ? styles.centerTabActive : ""}`}
              onClick={() => setSelectedCenter("all")}
            >
              Todos
            </button>
            {centers.map((center) => (
              <button
                key={center}
                type="button"
                className={`${styles.centerTab} ${selectedCenter === center ? styles.centerTabActive : ""}`}
                onClick={() => setSelectedCenter(center)}
              >
                {capitalize(center)}
              </button>
            ))}
          </div>
        </header>

        {error && <p className={styles.errorText}>{error}</p>}

        {!loading && filteredCoaches.length === 0 && (
          <p className={styles.emptyText}>No hay entrenadores para ese centro.</p>
        )}

        <div className={styles.coachList}>
          {filteredCoaches.map((coach) => {
            const isFavorite = favoriteIds.has(coach.id);
            const favoritePriority = favorites.find((item) => item.coach_id === coach.id)?.priority ?? null;
            const canAdd = !isFavorite && favorites.length < 3;
            const isBusy = savingCoachId === coach.id;

            return (
              <article key={coach.id} className={styles.coachItem}>
                <CoachAvatar coach={coach} />
                <div className={styles.coachInfo}>
                  <strong>{coach.name}</strong>
                  <span>
                    {isFavorite
                      ? `Favorito · prioridad ${favoritePriority ?? "-"}`
                      : coach.centro
                        ? `${capitalize(String(coach.centro))}`
                        : "Sin centro"}
                  </span>
                </div>

                {isFavorite ? (
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => {
                      void removeFavorite(coach.id);
                    }}
                    disabled={isBusy}
                  >
                    {isBusy ? <Loader2 size={16} className={styles.spin} /> : <StarOff size={16} />}
                    Quitar
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.addBtn}
                    onClick={() => {
                      if (nextPriority) {
                        void addOrMoveFavorite(coach.id, nextPriority);
                      }
                    }}
                    disabled={!canAdd || isBusy}
                  >
                    {isBusy ? <Loader2 size={16} className={styles.spin} /> : <Star size={16} />}
                    {canAdd ? `Anadir P${nextPriority}` : "Limite alcanzado"}
                  </button>
                )}

                {isFavorite && (
                  <span className={styles.statusPill}>
                    <Check size={12} />
                    Activo
                  </span>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function CoachAvatar({ coach }: { coach: Coach }) {
  const source = coach.profile_photo ? `${API_BASE}/api/profile_pic/${coach.profile_photo}` : "";
  const initials = coach.name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "C";

  if (source) {
    return <img src={source} alt={coach.name} className={styles.avatar} />;
  }

  return <div className={styles.avatarFallback}>{initials}</div>;
}

function capitalize(value: string): string {
  if (!value) {
    return "";
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}
